import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "../../../../components/ui/Button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useDispatch, useSelector } from "react-redux";
import {
    resetWizard,
    setSelectedSchedule,
    setWizardStep,
} from "../../../../redux/Slices/smartAdSchedulerSlice";
import { toggleDialog } from "../../../../redux/Slices/Global/globalSlice";
import { useCreateSmartAdSchedule } from "../../../../queries/smartAdScheduler";
import { useGetMatchSchedules } from "../../../../queries/matchScheduler";
import PhaseRulesStep from "./PhaseRulesStep";

const buildSteps = (t) => [
    t("smart_ads.wizard_step1"),
    t("smart_ads.wizard_step2"),
    t("smart_ads.wizard_step3"),
];

const PHASE_ORDER = ["pre_match", "first_half", "halftime", "second_half", "extra_time", "fulltime"];
const buildPhaseLabels = (t) => ({
    pre_match: t("smart_ads.phase_pre_match"), first_half: t("smart_ads.phase_first_half"),
    halftime: t("smart_ads.phase_halftime"), second_half: t("smart_ads.phase_second_half"),
    extra_time: t("smart_ads.phase_extra_time"), fulltime: t("smart_ads.phase_fulltime"),
});

export default function SmartAdScheduleDialog() {
    const { t }      = useTranslation();
    const dispatch   = useDispatch();
    const openDialog = useSelector((s) => s.global.openDialog);
    const { wizardStep, selectedSchedule, phaseRules } = useSelector((s) => s.smartAdScheduler);

    const STEPS       = buildSteps(t);
    const PHASE_LABELS = buildPhaseLabels(t);

    const createMutation = useCreateSmartAdSchedule();

    const { data: schedulesData, isLoading: schedulesLoading } = useGetMatchSchedules({ status: "assigned", per_page: 50 });
    const matchSchedules = Array.isArray(schedulesData)
        ? schedulesData
        : schedulesData?.data ?? [];

    const configuredRules = PHASE_ORDER.filter((p) => phaseRules[p]?.ad_campaign_id);

    const handleClose = () => {
        dispatch(resetWizard());
        dispatch(toggleDialog("SmartAdScheduleDialog"));
    };

    const handleSubmit = () => {
        if (!selectedSchedule || configuredRules.length === 0) return;

        const payload = {
            match_schedule_id: selectedSchedule.id,
            phase_rules: configuredRules.map((phase) => ({
                phase,
                ad_campaign_id:           phaseRules[phase].ad_campaign_id,
                display_duration_seconds: phaseRules[phase].display_duration_seconds ?? undefined,
            })),
        };

        createMutation.mutate(payload, { onSuccess: handleClose });
    };

    const canProceed = () => {
        if (wizardStep === 1) return !!selectedSchedule;
        if (wizardStep === 2) return configuredRules.length > 0;
        return true;
    };

    const renderStep = () => {
        switch (wizardStep) {
            case 1:
                return (
                    <div>
                        <p className="text-xs text-gray-500 mb-3">
                            {t("smart_ads.step1_hint")}
                        </p>
                        {schedulesLoading ? (
                            <div className="flex justify-center py-8"><ProgressSpinner /></div>
                        ) : matchSchedules.length === 0 ? (
                            <p className="text-center text-gray-400 text-sm py-8">
                                {t("smart_ads.no_match_schedules")}
                            </p>
                        ) : (
                            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                                {matchSchedules.map((ms) => {
                                    const fd = ms.fixture_data ?? {};
                                    const isSelected = selectedSchedule?.id === ms.id;
                                    return (
                                        <div
                                            key={ms.id}
                                            onClick={() => dispatch(setSelectedSchedule(ms))}
                                            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                                isSelected
                                                    ? "border-blue-500 bg-blue-50"
                                                    : "border-gray-200 hover:border-blue-300"
                                            }`}
                                        >
                                            {fd.league_logo && (
                                                <img src={fd.league_logo} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm text-gray-800">
                                                    {fd.home_team} <span className="text-gray-400 font-normal">vs</span> {fd.away_team}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {ms.kickoff_at
                                                        ? new Date(ms.kickoff_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
                                                        : "—"}
                                                </p>
                                            </div>
                                            {isSelected && <i className="pi pi-check-circle text-blue-600 flex-shrink-0" />}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );

            case 2:
                return <PhaseRulesStep />;

            case 3: {
                const fd = selectedSchedule?.fixture_data ?? {};
                return (
                    <div className="space-y-4 text-sm">
                        {/* Match */}
                        <div className="border rounded-lg p-3 bg-gray-50">
                            <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">{t("smart_ads.confirm_match_label")}</p>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {fd.home_logo && <img src={fd.home_logo} alt="" className="w-6 h-6 object-contain" />}
                                    <span className="font-semibold">{fd.home_team}</span>
                                </div>
                                <span className="text-gray-400 text-xs">vs</span>
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold">{fd.away_team}</span>
                                    {fd.away_logo && <img src={fd.away_logo} alt="" className="w-6 h-6 object-contain" />}
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                {selectedSchedule?.kickoff_at
                                    ? new Date(selectedSchedule.kickoff_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })
                                    : "—"}
                            </p>
                        </div>

                        {/* Phase rules */}
                        <div className="border rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">
                                {t("smart_ads.confirm_phase_rules_label", { count: configuredRules.length })}
                            </p>
                            <div className="space-y-1">
                                {configuredRules.map((phase) => {
                                    const rule = phaseRules[phase];
                                    return (
                                        <div key={phase} className="flex items-center justify-between text-xs">
                                            <span className="text-gray-600 w-24">{PHASE_LABELS[phase]}</span>
                                            <span className="font-medium text-gray-800 flex-1 truncate px-2">
                                                {t("smart_ads.ad_id_label")}: {rule.ad_campaign_id?.slice(0, 8)}…
                                            </span>
                                            {rule.display_duration_seconds && (
                                                <span className="text-gray-400">{rule.display_duration_seconds}s</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            }

            default:
                return null;
        }
    };

    return (
        <Dialog
            header={
                <div>
                    <p className="font-semibold text-gray-800">{t("smart_ads.dialog_title")}</p>
                    <div className="flex gap-1 mt-2">
                        {STEPS.map((label, i) => (
                            <div key={i} className="flex items-center gap-1">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                    i + 1 < wizardStep
                                        ? "bg-green-500 text-white"
                                        : i + 1 === wizardStep
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-200 text-gray-500"
                                }`}>
                                    {i + 1 < wizardStep
                                        ? <i className="pi pi-check" style={{ fontSize: "0.6rem" }} />
                                        : i + 1}
                                </div>
                                <span className={`text-xs ${i + 1 === wizardStep ? "text-blue-600 font-medium" : "text-gray-400"}`}>
                                    {label}
                                </span>
                                {i < STEPS.length - 1 && <span className="text-gray-300 mx-1">›</span>}
                            </div>
                        ))}
                    </div>
                </div>
            }
            visible={!!openDialog?.SmartAdScheduleDialog}
            onHide={handleClose}
            style={{ width: "620px" }}
            modal
            footer={
                <div className="flex justify-between">
                    <Button
                        label={t("buttons.back")}
                        icon="pi pi-arrow-left"
                        className="p-button-text p-button-sm"
                        disabled={wizardStep === 1}
                        onClick={() => dispatch(setWizardStep(wizardStep - 1))}
                    />
                    <div className="flex gap-2">
                        <Button
                            label={t("buttons.cancel")}
                            className="p-button-text p-button-sm"
                            onClick={handleClose}
                        />
                        {wizardStep < 3 ? (
                            <Button
                                label={t("buttons.next")}
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                className="p-button-primary p-button-sm"
                                disabled={!canProceed()}
                                onClick={() => dispatch(setWizardStep(wizardStep + 1))}
                            />
                        ) : (
                            <Button
                                label={t("smart_ads.create_schedule_btn")}
                                icon="pi pi-check"
                                className="p-button-success p-button-sm"
                                loading={createMutation.isLoading}
                                disabled={!canProceed()}
                                onClick={handleSubmit}
                            />
                        )}
                    </div>
                </div>
            }
        >
            <div className="pt-2">{renderStep()}</div>
        </Dialog>
    );
}
